import { useParams, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/pages/home/components/Footer";
import { kaspreadsheetUrl, seoArticlesList } from "@/mocks/categories";

const articleContents: Record<string, {
  heading: string;
  sections: { title: string; paragraphs: string[] }[];
  tips: { title: string; content: string }[];
  tableData: { name: string; cat: string; price: string; quality: string; trend: string }[];
  faqs: { q: string; a: string }[];
  metaModified: string;
  readingTime: string;
}> = {
  "guia-completo-hoobuyspreadsheet": {
    heading: "Guia Completo do Hoobuy Spreadsheet 2026: Como Encontrar os Melhores Produtos e Economizar",
    sections: [
      {
        title: "O Que É o Hoobuyspreadsheet e Por Que Você Precisa Dele",
        paragraphs: [
          "Se você está procurando produtos de moda streetwear, tênis exclusivos e acessórios com os melhores preços do mercado, o hoobuyspreadsheet é exatamente o que você precisa. Trata-se de uma planilha organizada e constantemente atualizada que reúne os produtos mais virais e desejados do momento, com informações detalhadas sobre preços, qualidade, tendências e links diretos para compra.",
          "Diferente de outras listas genéricas espalhadas pela internet, o hoobuy spreadsheet é mantido por uma comunidade ativa que testa e verifica cada produto antes de adicioná-lo à lista. Isso significa que você tem acesso a informações confiáveis e atualizadas, economizando tempo e dinheiro nas suas compras online.",
          "A cada semana, novos produtos são adicionados ao hoobuy sheet, garantindo que você sempre tenha acesso às últimas tendências e lançamentos. Seja você um entusiasta de moda streetwear, um colecionador de tênis ou simplesmente alguém que busca boas ofertas, esta ferramenta foi criada para facilitar sua vida."
        ]
      },
      {
        title: "Como Funciona o Hoobuy Sheet: Estrutura e Organização",
        paragraphs: [
          "O hoobuy sheet é organizado em categorias claras e intuitivas, facilitando a navegação e a busca pelos produtos que você mais deseja. A estrutura foi pensada para que qualquer pessoa, mesmo sem experiência prévia, consiga encontrar rapidamente o que procura.",
          "Cada categoria do hoobuyspreadsheet contém dezenas de produtos cuidadosamente selecionados. Para cada item, você encontra informações como nome do produto, preço estimado, avaliação de qualidade, nível de tendência e um link direto para visualização e compra."
        ]
      },
      {
        title: "Vantagens de Usar o Hoobuyspreadsheet em 2026",
        paragraphs: []
      },
      {
        title: "Hoobuyspreadsheet é Confiável? Nossa Análise",
        paragraphs: [
          "Esta é uma das perguntas mais frequentes entre novos usuários. A resposta curta é: sim, o hoobuyspreadsheet é confiável. Mas vamos explicar o porquê.",
          "Em primeiro lugar, a planilha é mantida por uma comunidade ativa de usuários que testam e verificam cada produto antes de adicioná-lo. Isso cria um sistema de controle de qualidade descentralizado, onde informações imprecisas são rapidamente identificadas e corrigidas.",
          "Em segundo lugar, o hoobuy sheet é transparente quanto às suas fontes e métodos. Todos os preços são verificados regularmente, e os links direcionam para as páginas oficiais dos produtos.",
          "Por fim, a longevidade do projeto — ativo há anos e com milhares de usuários satisfeitos — é o maior indicador de confiabilidade. Se o hoobuyspreadsheet não funcionasse, simplesmente não teria a base de usuários fiéis que construiu ao longo do tempo."
        ]
      },
      {
        title: "Perguntas Frequentes Sobre o Hoobuyspreadsheet",
        paragraphs: []
      }
    ],
    tips: [
      { title: "Economia de Tempo", content: "Em vez de passar horas navegando em dezenas de sites e fóruns, o hoobuyspreadsheet concentra todas as informações em um único local organizado. Você economiza tempo precioso e encontra exatamente o que precisa em minutos." },
      { title: "Produtos Verificados", content: "Cada produto listado no hoobuy sheet passa por um processo de verificação da comunidade, garantindo que as informações sobre qualidade, preço e disponibilidade sejam precisas e confiáveis." },
      { title: "Atualização Constante", content: "O hoobuyspreadsheet é atualizado semanalmente com novos produtos, tendências e informações. Você nunca fica para trás nas últimas novidades do mundo da moda streetwear e acessórios." },
      { title: "Melhores Preços", content: "A comunidade do hoobuy spreadsheet está sempre atenta às melhores ofertas e promoções, compartilhando os preços mais competitivos para cada produto listado." }
    ],
    tableData: [
      { name: "Nike Tech Fleece", cat: "Streetwear", price: "€35", quality: "Alta", trend: "🔥🔥🔥🔥🔥" },
      { name: "Air Force 1", cat: "Sneakers", price: "€40", quality: "Alta", trend: "🔥🔥🔥🔥" },
      { name: "Essentials Hoodie", cat: "Moda", price: "€30", quality: "Alta", trend: "🔥🔥🔥🔥🔥" },
      { name: "Carhartt Cargo", cat: "Pants", price: "€28", quality: "Média-Alta", trend: "🔥🔥🔥🔥" },
      { name: "New Balance 550", cat: "Sneakers", price: "€45", quality: "Alta", trend: "🔥🔥🔥🔥🔥" }
    ],
    faqs: [
      { q: "O hoobuyspreadsheet é gratuito?", a: "Sim! O acesso ao hoobuyspreadsheet é completamente gratuito. Você pode navegar por todas as categorias, conferir produtos e preços sem pagar nada." },
      { q: "Com que frequência o hoobuy sheet é atualizado?", a: "O hoobuy sheet recebe atualizações semanais com novos produtos, ajustes de preços e tendências atualizadas. A comunidade está sempre contribuindo com novas descobertas." },
      { q: "Posso confiar nos preços listados?", a: "Os preços no hoobuyspreadsheet são verificados regularmente pela comunidade. No entanto, como o mercado é dinâmico, recomendamos sempre conferir o preço final no link do produto." },
      { q: "Como posso contribuir com o hoobuyspreadsheet?", a: "A comunidade do hoobuyspreadsheet está sempre aberta a novos colaboradores. Você pode sugerir produtos, reportar informações desatualizadas ou compartilhar suas descobertas." }
    ],
    metaModified: "2026-06-25",
    readingTime: "8 minutos"
  },
  "hoobuy-finds-produtos-virais": {
    heading: "Hoobuy Finds: Os Produtos Mais Virais do Momento",
    sections: [
      {
        title: "Os Produtos que Estão Bombando no Hoobuyspreadsheet",
        paragraphs: [
          "O hoobuyspreadsheet é conhecido por reunir os produtos mais virais da internet em um só lugar. Esta semana não é diferente: temos uma seleção incrível de itens que estão fazendo sucesso entre os compradores mais antenados.",
          "De tênis exclusivos a moletons estilosos, passando por acessórios que todo mundo quer, o hoobuy sheet está recheado de oportunidades imperdíveis para quem busca estilo e economia."
        ]
      },
      {
        title: "Como Identificar um Produto Viral",
        paragraphs: [
          "No hoobuyspreadsheet, os produtos virais se destacam por alguns indicadores claros: alta pontuação de tendência, grande volume de avaliações positivas e um crescimento rápido no número de menções pela comunidade.",
          "Fique de olho nos produtos marcados com indicadores de tendência máxima — são esses que geralmente se tornam os itens mais desejados da temporada."
        ]
      },
      {
        title: "Top Categorias de Produtos Virais",
        paragraphs: [
          "Tênis e sneakers continuam liderando como a categoria mais quente do hoobuyspreadsheet. Modelos retrô e colaborações exclusivas são os mais procurados.",
          "Moletons e hoodies oversized também estão em alta, especialmente os modelos com design minimalista e cores neutras. A categoria de acessórios vem crescendo rapidamente, com destaque para bolsas e óculos."
        ]
      }
    ],
    tips: [
      { title: "Aja Rápido", content: "Produtos virais no hoobuyspreadsheet tendem a se esgotar rapidamente. Quando encontrar algo que gostou, não demore muito para decidir." },
      { title: "Compare Preços", content: "Use o hoobuy sheet para comparar preços entre diferentes vendedores do mesmo produto. Às vezes a diferença é significativa." },
      { title: "Siga as Tendências", content: "O indicador de tendência do hoobuyspreadsheet é atualizado semanalmente. Acompanhe para saber o que está em alta." }
    ],
    tableData: [
      { name: "Dunk Low Retro", cat: "Sneakers", price: "€42", quality: "Alta", trend: "🔥🔥🔥🔥🔥" },
      { name: "Yeezy Slide", cat: "Shoes", price: "€25", quality: "Média-Alta", trend: "🔥🔥🔥🔥🔥" },
      { name: "Gallery Dept Tee", cat: "T-Shirts", price: "€20", quality: "Alta", trend: "🔥🔥🔥🔥" },
      { name: "Stussy Hoodie", cat: "Hoodies", price: "€32", quality: "Alta", trend: "🔥🔥🔥🔥🔥" }
    ],
    faqs: [
      { q: "Os produtos virais são de qualidade?", a: "Sim, os produtos listados no hoobuyspreadsheet como virais passam por verificação da comunidade, garantindo um nível mínimo de qualidade." },
      { q: "Com que frequência a lista de virais muda?", a: "A lista é atualizada semanalmente, refletindo as mudanças nas tendências e novos produtos que ganham destaque." }
    ],
    metaModified: "2026-06-24",
    readingTime: "5 minutos"
  },
  "guia-compras-hoobuy-iniciantes": {
    heading: "Guia de Compras Hoobuy para Iniciantes: Passo a Passo Completo",
    sections: [
      {
        title: "Primeiros Passos no Hoobuyspreadsheet",
        paragraphs: [
          "Se você nunca usou o hoobuyspreadsheet antes, não se preocupe — este guia foi feito especialmente para você. Vamos explicar tudo que você precisa saber para começar a usar a planilha com confiança e segurança.",
          "O hoobuyspreadsheet é uma ferramenta incrivelmente útil para quem busca produtos de moda com bons preços, mas pode ser intimidante à primeira vista. Com este guia, você estará navegando como um profissional em poucos minutos."
        ]
      },
      {
        title: "Como Navegar pelas Categorias",
        paragraphs: [
          "A primeira coisa que você verá no hoobuy sheet são as 14 categorias de produtos. Cada categoria agrupa itens similares, facilitando sua busca. Clique na categoria que mais te interessa para começar.",
          "Dentro de cada categoria, os produtos são listados com informações como nome, preço, qualidade e tendência. Use essas informações para comparar e escolher os melhores itens."
        ]
      },
      {
        title: "Entendendo as Informações de Cada Produto",
        paragraphs: [
          "Cada produto no hoobuyspreadsheet vem com uma ficha completa: nome do item, preço estimado, nível de qualidade (verificado pela comunidade), indicador de tendência e link para compra.",
          "O nível de qualidade é especialmente importante para iniciantes — produtos com qualidade Alta ou Média-Alta são apostas mais seguras para sua primeira compra."
        ]
      }
    ],
    tips: [
      { title: "Comece pelas Categorias Populares", content: "Tênis e Moletons são ótimas categorias para iniciantes no hoobuyspreadsheet. São produtos com muita informação disponível e avaliações confiáveis." },
      { title: "Verifique a Qualidade", content: "Sempre confira o indicador de qualidade antes de comprar. Produtos com qualidade Alta são mais confiáveis." },
      { title: "Use os Links com Atenção", content: "Os links do hoobuy sheet levam diretamente aos produtos. Sempre verifique se o link está correto antes de finalizar a compra." }
    ],
    tableData: [
      { name: "Basic Tee", cat: "T-Shirts", price: "€12", quality: "Média-Alta", trend: "🔥🔥🔥" },
      { name: "Classic Hoodie", cat: "Hoodies", price: "€25", quality: "Alta", trend: "🔥🔥🔥🔥" },
      { name: "Simple Sneakers", cat: "Shoes", price: "€30", quality: "Alta", trend: "🔥🔥🔥🔥" }
    ],
    faqs: [
      { q: "Preciso de conta para usar o hoobuyspreadsheet?", a: "Não! O hoobuyspreadsheet é acessível sem necessidade de cadastro. Basta acessar e começar a explorar." },
      { q: "É seguro comprar pelos links do hoobuy sheet?", a: "Os links direcionam para as páginas oficiais dos produtos. A segurança da compra em si depende da plataforma de destino." }
    ],
    metaModified: "2026-06-23",
    readingTime: "6 minutos"
  }
};

const defaultContent = articleContents["guia-completo-hoobuyspreadsheet"];

const categoriesList = [
  "Tênis (Shoes)", "Moletons (Hoodies/Sweaters)", "Camisetas (T-Shirts)",
  "Jaquetas (Jackets)", "Calças e Bermudas (Pants/Shorts)", "Acessórios de Cabeça (Headwear)",
  "Conjuntos (Sets)", "Roupa Íntima (Underwear)", "Camisas Esportivas (Jersey)",
  "Acessórios (Accessories)", "Bolsas (Bags)", "Cintos (Belts)",
  "Perfumes (Perfumes)", "Outros Itens (Other Stuff)"
];

const dicasGerais = [
  { title: "1. Verifique as Atualizações Semanais", content: "O hoobuyspreadsheet é atualizado toda semana com novos produtos e informações. Crie o hábito de conferir as novidades regularmente para não perder as melhores oportunidades e lançamentos exclusivos." },
  { title: "2. Use os Filtros por Categoria", content: "Com 14 categorias disponíveis no hoobuy sheet, você pode filtrar exatamente o tipo de produto que está procurando. Seja tênis, moletons, jaquetas ou acessórios, a navegação por categoria torna a experiência muito mais eficiente." },
  { title: "3. Compare Preços e Qualidade", content: "Uma das maiores vantagens do hoobuyspreadsheet é a possibilidade de comparar preços e qualidade entre diferentes produtos da mesma categoria. Use as informações da tabela para fazer escolhas mais inteligentes." },
  { title: "4. Acompanhe as Tendências", content: "O indicador de tendência do hoobuy sheet mostra quais produtos estão bombando no momento. Fique de olho nos itens com maior pontuação de tendência para estar sempre por dentro do que há de mais atual no mundo da moda." }
];

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" as const });
  }, [slug]);

  const article = seoArticlesList.find((a) => a.slug === slug) || seoArticlesList[0];
  const content = articleContents[slug || ""] || defaultContent;

  return (
    <main>
      <Navbar />

      <article className="pt-[72px]">
        <header className="relative w-full h-[420px] md:h-[500px] overflow-hidden">
          <img
            src={article.image}
            alt={article.title}
            title={article.title}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/20 to-black/50"></div>
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12">
            <div className="max-w-4xl mx-auto">
              <span className="inline-block px-3 py-1.5 rounded-full bg-accent-500 text-white text-xs font-semibold tracking-wide uppercase mb-4">
                {article.tag}
              </span>
              <h1 className="text-2xl md:text-4xl lg:text-5xl font-extrabold text-white font-heading leading-[1.15] mb-4">
                {content.heading}
              </h1>
              <p className="text-white/70 text-sm md:text-base">
                Atualizado em {content.metaModified} • Leitura de {content.readingTime}
              </p>
            </div>
          </div>
        </header>

        <div className="max-w-4xl mx-auto px-4 md:px-8 py-12 md:py-16">
          <nav className="mb-10 text-sm text-foreground-500" aria-label="Breadcrumb">
            <a href="/" className="hover:text-foreground-800 transition-colors">Início</a>
            <span className="mx-2">/</span>
            <span className="text-foreground-700 font-medium">{article.title}</span>
          </nav>

          <section className="prose-custom space-y-8">
            {content.sections.map((section, sIdx) => (
              <div key={sIdx}>
                {sIdx === 2 ? (
                  <>
                    <h2 className="text-2xl md:text-3xl font-extrabold text-foreground-950 font-heading mb-5">
                      {section.title}
                    </h2>
                    <div className="space-y-5">
                      {content.tips.map((tip, tIdx) => (
                        <div key={tIdx} className="flex gap-4">
                          <div className="w-8 h-8 rounded-full bg-secondary-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <span className="text-secondary-600 text-sm font-bold">{tIdx + 1}</span>
                          </div>
                          <div>
                            <h3 className="text-lg font-bold text-foreground-900 font-heading mb-1.5">{tip.title}</h3>
                            <p className="text-foreground-600 text-sm leading-relaxed">{tip.content}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                ) : section.paragraphs.length > 0 ? (
                  <>
                    <h2 className="text-2xl md:text-3xl font-extrabold text-foreground-950 font-heading mb-5">
                      {section.title}
                    </h2>
                    {section.paragraphs.map((p, pIdx) => (
                      <p key={pIdx} className="text-foreground-600 text-base leading-relaxed mb-4">{p}</p>
                    ))}
                  </>
                ) : null}
              </div>
            ))}

            <div className="bg-accent-50 border border-accent-100 rounded-2xl p-6 md:p-8 my-10">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-accent-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-lightbulb-line text-white text-lg"></i>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-foreground-900 font-heading mb-2">Resumo Rápido</h3>
                  <p className="text-foreground-600 text-sm leading-relaxed">
                    O <strong>hoobuyspreadsheet</strong> é a planilha mais completa de produtos virais do momento. Com mais de 5.000 itens indexados em 14 categorias diferentes, atualizações semanais e uma comunidade ativa de verificadores, você tem acesso aos melhores produtos com os melhores preços em um só lugar.
                  </p>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-xl font-bold text-foreground-900 font-heading mt-8 mb-3">
                Categorias Disponíveis no Hoobuyspreadsheet
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
                {categoriesList.map((cat) => (
                  <div key={cat} className="flex items-center gap-2.5 px-4 py-3 bg-background-100 rounded-xl">
                    <i className="ri-check-line text-accent-500 text-sm"></i>
                    <span className="text-foreground-700 text-sm">{cat}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-background-100 rounded-2xl p-6 md:p-8 my-10">
              <h3 className="text-xl font-bold text-foreground-950 font-heading mb-4">
                Produtos em Destaque no Hoobuyspreadsheet
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-background-300">
                      <th className="text-left py-3 px-3 text-foreground-500 font-medium text-xs uppercase tracking-wider">Produto</th>
                      <th className="text-left py-3 px-3 text-foreground-500 font-medium text-xs uppercase tracking-wider">Categoria</th>
                      <th className="text-left py-3 px-3 text-foreground-500 font-medium text-xs uppercase tracking-wider">Preço</th>
                      <th className="text-left py-3 px-3 text-foreground-500 font-medium text-xs uppercase tracking-wider">Qualidade</th>
                      <th className="text-left py-3 px-3 text-foreground-500 font-medium text-xs uppercase tracking-wider">Tendência</th>
                      <th className="text-left py-3 px-3 text-foreground-500 font-medium text-xs uppercase tracking-wider">Link</th>
                    </tr>
                  </thead>
                  <tbody>
                    {content.tableData.map((item, idx) => (
                      <tr key={idx} className="border-b border-background-200/70 hover:bg-background-50 transition-colors">
                        <td className="py-3 px-3 text-foreground-800 font-medium">{item.name}</td>
                        <td className="py-3 px-3 text-foreground-500">{item.cat}</td>
                        <td className="py-3 px-3 text-foreground-700 font-medium">{item.price}</td>
                        <td className="py-3 px-3">
                          <span className="inline-block px-2 py-0.5 rounded-full bg-accent-100 text-accent-700 text-xs font-medium">{item.quality}</span>
                        </td>
                        <td className="py-3 px-3 text-sm">{item.trend}</td>
                        <td className="py-3 px-3">
                          <a href={kaspreadsheetUrl} target="_blank" rel="noopener nofollow" className="text-accent-600 text-xs font-medium hover:text-accent-700 whitespace-nowrap">Ver →</a>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-foreground-400 text-xs mt-4">
                * Preços e tendências atualizados semanalmente no <strong>hoobuyspreadsheet</strong>
              </p>
            </div>

            <div>
              <h2 className="text-2xl md:text-3xl font-extrabold text-foreground-950 font-heading mb-5">
                Dicas Para Aproveitar ao Máximo o Hoobuy Sheet
              </h2>
              {dicasGerais.map((dica, idx) => (
                <div key={idx}>
                  <h3 className="text-lg font-bold text-foreground-900 font-heading mt-6 mb-3">{dica.title}</h3>
                  <p className="text-foreground-600 text-base leading-relaxed mb-4">{dica.content}</p>
                </div>
              ))}
            </div>

            <div className="bg-primary-500 rounded-2xl p-8 md:p-10 my-10 text-center">
              <h3 className="text-2xl md:text-3xl font-extrabold text-white font-heading mb-4">
                Pronto para Explorar o Hoobuyspreadsheet?
              </h3>
              <p className="text-white/75 text-base mb-8 max-w-lg mx-auto">
                Acesse agora o <strong className="text-white">hoobuyspreadsheet</strong> mais completo da internet e descubra os produtos virais que todo mundo está comentando.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <a href={kaspreadsheetUrl} target="_blank" rel="noopener nofollow" className="inline-flex items-center gap-2.5 px-8 py-4 bg-white text-primary-700 text-base font-bold rounded-full hover:bg-background-50 transition-all hover:scale-[1.02] whitespace-nowrap cursor-pointer">
                  hoobuy sheet
                  <i className="ri-arrow-right-up-line text-lg"></i>
                </a>
                <a href={kaspreadsheetUrl} target="_blank" rel="noopener nofollow" className="inline-flex items-center gap-2.5 px-8 py-4 bg-primary-600 text-white text-base font-semibold rounded-full border border-white/20 hover:bg-primary-700 transition-all hover:scale-[1.02] whitespace-nowrap cursor-pointer">
                  buy it now
                  <i className="ri-arrow-right-up-line text-lg"></i>
                </a>
              </div>
            </div>

            <div>
              <h2 className="text-2xl md:text-3xl font-extrabold text-foreground-950 font-heading mb-5">
                Perguntas Frequentes
              </h2>
              <div className="space-y-4">
                {content.faqs.map((faq, idx) => (
                  <details key={idx} className="group bg-background-100 rounded-xl border border-background-200/70 overflow-hidden">
                    <summary className="flex items-center justify-between px-5 py-4 cursor-pointer list-none">
                      <span className="text-foreground-800 font-medium text-sm pr-4">{faq.q}</span>
                      <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform flex-shrink-0"></i>
                    </summary>
                    <div className="px-5 pb-4">
                      <p className="text-foreground-500 text-sm leading-relaxed">{faq.a}</p>
                    </div>
                  </details>
                ))}
              </div>
            </div>

            <div className="border-t border-background-200/70 pt-8 mt-12">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                  <p className="text-foreground-600 text-sm mb-1">
                    Gostou do artigo? Volte para a página inicial e explore todas as categorias do hoobuyspreadsheet.
                  </p>
                  <button type="button" onClick={() => navigate("/")} className="inline-flex items-center gap-1.5 text-accent-600 text-sm font-medium hover:text-accent-700 transition-colors cursor-pointer">
                    <i className="ri-arrow-left-line"></i>
                    Voltar para o Início
                  </button>
                </div>
                <a href={kaspreadsheetUrl} target="_blank" rel="noopener nofollow" className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 text-sm font-semibold rounded-full hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer">
                  Acessar Hoobuy Sheet
                  <i className="ri-arrow-right-up-line"></i>
                </a>
              </div>
            </div>
          </section>
        </div>
      </article>

      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: (() => {
            const siteUrl = import.meta.env.VITE_SITE_URL || "https://hoobuy.biz";
            const schemas: object[] = [
              {
                "@context": "https://schema.org",
                "@type": "Article",
                headline: content.heading,
                description: article.excerpt,
                image: article.image,
                datePublished: "2026-06-20",
                dateModified: content.metaModified,
                author: { "@type": "Organization", name: "hoobuy.biz" },
                publisher: {
                  "@type": "Organization",
                  name: "hoobuy.biz",
                  logo: {
                    "@type": "ImageObject",
                    url: "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/879fae149d7d175a673508cd1be74143.jpeg"
                  }
                },
                inLanguage: "pt-BR",
                mainEntityOfPage: {
                  "@type": "WebPage",
                  "@id": `${siteUrl}/artigo/${article.slug}`
                }
              }
            ];
            if (content.faqs && content.faqs.length > 0) {
              schemas.push({
                "@context": "https://schema.org",
                "@type": "FAQPage",
                mainEntity: content.faqs.map((faq: { q: string; a: string }) => ({
                  "@type": "Question",
                  name: faq.q,
                  acceptedAnswer: {
                    "@type": "Answer",
                    text: faq.a
                  }
                }))
              });
            }
            return JSON.stringify(schemas.length === 1 ? schemas[0] : { "@graph": schemas });
          })()
        }}
      />
    </main>
  );
}